import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, ListGroup, Alert, Placeholder } from 'react-bootstrap';
import { auth } from '../../firebase';
import './ComicDetail.css';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// This is the new Skeleton component for the loading state.
const ComicDetailSkeleton = () => (
  <Container>
    {/* Placeholder for the title */}
    <Placeholder as="h2" animation="glow" className="mb-4">
      <Placeholder xs={6} />
    </Placeholder>
    {/* Placeholders for the chapter list */}
    <ListGroup>
      {[...Array(5)].map((_, i) => (
        <ListGroup.Item key={i}>
          <Placeholder as="span" animation="glow">
            <Placeholder xs={8} />
          </Placeholder>
        </ListGroup.Item>
      ))}
    </ListGroup>
  </Container>
);

const ComicDetail = () => {
  const { book_id } = useParams();
  const [comic, setComic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchComicData = async () => {
      try {
        // Reset state for each new request
        setLoading(true);
        setError(null);
        setComic(null);

        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated.");
        }
        const token = await user.getIdToken();

        const response = await fetch(`${backendUrl}/api/comics/${book_id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-API-Key': process.env.REACT_APP_API_KEY
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch comics data.');
        }
        
        // Simulate a network delay to make the skeleton visible
        await new Promise(resolve => setTimeout(resolve, 500));

        const data = await response.json();
        setComic(data); // This will be null if not found, which we handle below

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComicData();
  }, [book_id]);

  // 1. Show the skeleton UI while loading
  if (loading) {
    return <ComicDetailSkeleton />;
  }

  // 2. Show an error message if the fetch failed
  if (error) {
    return (
      <div className="error-container">
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  // 3. Show a "not found" message if loading is done and the comic is still null
  if (!comic) {
    return (
        <div className="error-container">
            <Alert variant="warning">Sorry, the requested comic could not be found.</Alert>
        </div>
    );
  }

  // 4. Show the actual content once everything has loaded successfully
  return (
    <>
      <Container className="pb-16">
        <h2 className="mb-4 text-center">{comic.title}</h2>
        <ListGroup>
          {comic.chapters.map(chapter => (
            <ListGroup.Item
              key={chapter.id}
              as={Link}
              to={`/comic/${comic.book_id}/chapter/${chapter.id}`}
              action // This prop from react-bootstrap adds hover/focus styles
              className="chapter-list-item"
            >
              {chapter.title} ({chapter.pages.length} pages)
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Container>
    </>
  );
};

export default ComicDetail;
